/**
 * LeadMapView — the schematic locator Lens (AD-046 §7.5, locked L-2:
 * schematic for the pilot, zero new geo data; live Google Maps is a
 * fast-follow that adds city/coords). Pins are clustered from the existing
 * free-text `location` string, coloured by the folded source. Click a
 * cluster → its leads → "View record →" opens the shared drawer. Unlocated
 * leads sit in their own tray. Mobile = the same clustering, scaled panel.
 */
import React, { useMemo, useState } from 'react';
import { type Lead } from '../../lib/leads';
import { clusterByLocation, sourceColour, sourceMap } from '../../lib/leadLenses';

export default function LeadMapView({
  leads,
  onSelect,
  selectedId,
}: {
  leads: Lead[];
  onSelect: (id: string) => void;
  /** Currently-open lead — its pin / row gets the `selected` treatment. */
  selectedId?: string | null;
  isMobile?: boolean;
}) {
  const clusters = useMemo(() => clusterByLocation(leads), [leads]);
  const [open, setOpen] = useState<string | null>(null);

  const located = clusters.filter((c) => c.key);
  const unlocated = clusters.find((c) => !c.key);

  return (
    <div style={{ padding: 12 }}>
      <div style={{ fontSize: 11, color: 'var(--ink-mute, #6B7280)', marginBottom: 8 }}>
        Schematic locator — leads clustered by their <b>location</b> text (not live geo).
        Live Google Maps is a fast-follow.
      </div>

      {located.length === 0 ? (
        <div style={emptyS}>No located leads (no <code>location</code> text yet).</div>
      ) : (
        <div
          style={{
            position: 'relative',
            border: '1px solid var(--rule, #E5E7EB)',
            borderRadius: 'var(--r-md, 13px)',
            background:
              'repeating-linear-gradient(0deg, var(--inset, #F6F7F9), var(--inset, #F6F7F9) 19px, var(--inset-deep, #EEF0F3) 20px),' +
              'repeating-linear-gradient(90deg, transparent, transparent 19px, var(--inset-deep, #EEF0F3) 20px)',
            padding: 16,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 14,
            minHeight: 200,
            alignContent: 'flex-start',
          }}
        >
          {located.map((c) => {
            const colour = sourceColour(c.leads[0].source);
            const size = Math.min(56, 26 + c.leads.length * 4);
            // The pin is "selected" when the open lead lives inside this cluster.
            const pinSelected = selectedId != null && c.leads.some((l) => l.id === selectedId);
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setOpen(open === c.key ? null : c.key)}
                title={`${c.label} · ${c.leads.length} lead(s)`}
                aria-current={pinSelected ? true : undefined}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 3,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    width: size,
                    height: size,
                    borderRadius: '50%',
                    background: `${colour}22`,
                    // Selected pin: accent ring + soft accent elevation.
                    border: pinSelected ? '2px solid var(--accent, #008069)' : `2px solid ${colour}`,
                    boxShadow: pinSelected
                      ? '0 0 0 2px var(--accent, #008069), 0 8px 22px -10px rgba(0,128,105,.55)'
                      : undefined,
                    color: colour,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: 13,
                  }}
                >
                  {c.leads.length}
                </span>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-soft, #4B5563)', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.label}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* cluster popup — the leads in the open cluster */}
      {open && (() => {
        const c = located.find((x) => x.key === open);
        if (!c) return null;
        return (
          <div style={{ marginTop: 10, border: '1px solid var(--rule, #E5E7EB)', borderRadius: 'var(--r-md, 13px)', overflow: 'hidden' }}>
            <div style={{ padding: '6px 10px', background: 'var(--inset, #F6F7F9)', fontWeight: 800, fontSize: 12 }}>
              {c.label} · {c.leads.length}
            </div>
            {c.leads.map((l) => {
              const selected = l.id === selectedId;
              return (
                <div
                  key={l.id}
                  data-lead-id={l.id}
                  aria-current={selected ? true : undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    padding: '7px 10px',
                    borderTop: '1px solid var(--rule, #E5E7EB)',
                    // Selected row: accent wash + soft accent elevation.
                    background: selected ? 'var(--accent-wash, #E9F7EF)' : undefined,
                    boxShadow: selected ? 'inset 2px 0 0 var(--accent, #008069)' : undefined,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 12 }}>{l.name}</div>
                    <span style={{ fontSize: 9.5, fontWeight: 700, color: sourceColour(l.source) }}>{sourceMap(l.source)}</span>
                  </div>
                  <button type="button" onClick={() => onSelect(l.id)} style={viewBtn}>View record →</button>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* unlocated tray */}
      {unlocated && unlocated.leads.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink-mute, #6B7280)', margin: '4px 2px' }}>
            Unlocated · {unlocated.leads.length}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {unlocated.leads.map((l) => {
              const selected = l.id === selectedId;
              return (
                <button
                  key={l.id}
                  type="button"
                  data-lead-id={l.id}
                  onClick={() => onSelect(l.id)}
                  aria-current={selected ? true : undefined}
                  style={{
                    ...viewBtn,
                    borderColor: selected ? 'var(--accent, #008069)' : sourceColour(l.source),
                    borderWidth: selected ? 2 : 1,
                    color: selected ? 'var(--accent-deep, #075E54)' : sourceColour(l.source),
                    boxShadow: selected ? '0 8px 22px -10px rgba(0,128,105,.55)' : undefined,
                  }}
                >
                  {l.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const viewBtn: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  padding: '4px 10px',
  borderRadius: 'var(--r-pill, 999px)',
  border: '1px solid var(--accent, #008069)',
  background: 'var(--paper, #fff)',
  color: 'var(--accent-deep, #075E54)',
  cursor: 'pointer',
};
const emptyS: React.CSSProperties = {
  padding: '30px 12px',
  textAlign: 'center',
  color: 'var(--ink-mute, #6B7280)',
  fontSize: 13,
};
